import React from 'react';
import { Box, useTheme } from '@mui/material';

/**
 * Numeric scale ruler drawn between the group bands and the cells.
 *
 *   1       1.5       2       2.5       3       3.5       4       4.5       5
 *   |                  |                 |                 |                  |
 *   ─────────────────────────────────────────────────────────────────────────
 *
 * Layout (total width = same as the cells row = 4 * groupCellsWidth + 3 * groupGap):
 *   - Integer ticks "1" / "5" sit at the very left/right edges.
 *   - Integer ticks "2" / "3" / "4" sit at the midpoint of the gap between groups.
 *   - Half labels (1.5 / 2.5 / 3.5 / 4.5) sit at the geometric center of their group.
 */
const GraduatedScale = ({ cellSize, cellGap, groupGap }) => {
  const theme = useTheme();

  const groupCellsWidth = 4 * cellSize + 3 * cellGap;
  const totalWidth = 4 * groupCellsWidth + 3 * groupGap;

  // Integer ticks: 1, 2, 3, 4, 5 — at edges and at gap midpoints between groups
  const integerXs = [
    0,
    groupCellsWidth + groupGap / 2,
    2 * groupCellsWidth + 1.5 * groupGap,
    3 * groupCellsWidth + 2.5 * groupGap,
    totalWidth
  ];
  const integerLabels = [1, 2, 3, 4, 5];

  // Half labels: at the center of each group
  const halfData = [1.5, 2.5, 3.5, 4.5].map((value, i) => ({
    value,
    x: i * (groupCellsWidth + groupGap) + groupCellsWidth / 2
  }));

  const lineColor = theme.palette.primary.main;
  const tickHeight = 6;
  const lineThickness = 1.5;
  const numbersHeight = Math.max(14, Math.round(cellSize * 0.7));
  const labelFs = `${Math.max(11, Math.round(cellSize * 0.55))}px`;
  const halfFs = `${Math.max(9, Math.round(cellSize * 0.4))}px`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: `${totalWidth}px` }}>
      {/* Numbers row */}
      <Box sx={{ position: 'relative', width: `${totalWidth}px`, height: `${numbersHeight}px` }}>
        {integerLabels.map((n, i) => (
          <Box
            key={`int-${n}`}
            sx={{
              position: 'absolute',
              left: `${integerXs[i]}px`,
              top: 0,
              transform: 'translateX(-50%)',
              fontSize: labelFs,
              fontWeight: 700,
              lineHeight: 1.1,
              color: 'text.primary'
            }}
          >
            {n}
          </Box>
        ))}
        {halfData.map(({ value, x }) => (
          <Box
            key={`half-${value}`}
            sx={{
              position: 'absolute',
              left: `${x}px`,
              top: 0,
              transform: 'translateX(-50%)',
              fontSize: halfFs,
              fontWeight: 400,
              lineHeight: 1.1,
              color: 'text.secondary'
            }}
          >
            {value}
          </Box>
        ))}
      </Box>

      {/* Baseline + ticks */}
      <Box sx={{ position: 'relative', width: `${totalWidth}px`, height: `${tickHeight}px`, mt: 0.25 }}>
        <Box
          sx={{
            position: 'absolute',
            left: `${integerXs[0]}px`,
            width: `${integerXs[integerXs.length - 1] - integerXs[0]}px`,
            top: '50%',
            height: `${lineThickness}px`,
            backgroundColor: lineColor,
            transform: 'translateY(-50%)'
          }}
        />
        {integerXs.map((x, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: `${x - 1}px`,
              top: 0,
              width: '2px',
              height: `${tickHeight}px`,
              backgroundColor: lineColor
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default GraduatedScale;
